import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Badge } from '../components/ui/Badge';
import { useEvent, useUser, useAuth } from '../store';
import { createParticipant, loadEvent as loadEventFromStorage, saveEvent, saveCurrentUser } from '../utils';
import { addEventToUser, loadEvent as loadEventFromFirestore } from '../services/firestore';
import type { Event } from '../types';

/**
 * Join Page
 * Event landing page - shows event details and allows users to join
 * If not logged in, prompts to login/signup first
 */
export function JoinPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { loadEvent, addParticipant } = useEvent();
  const { setCurrentParticipant, setEventId } = useUser();
  const { currentUser, isAuthenticated } = useAuth();
  
  const [eventData, setEventData] = useState<Event | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  // Load event from Firestore (no auth required to view)
  useEffect(() => {
    if (!eventId) {
      setError('Invalid event ID');
      setLoading(false);
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      
      try {
        const event = await loadEventFromStorage(eventId);
        if (event) {
          setEventData(event);
          loadEvent(event);
          setEventId(eventId);
          
          // Check if user already joined - if so, redirect directly
          if (currentUser && isAuthenticated) {
            const alreadyJoined = event.participants.some(
              (p) => p.name.toLowerCase() === currentUser.username.toLowerCase()
            );
            
            if (alreadyJoined) {
              const existingParticipant = event.participants.find(
                (p) => p.name.toLowerCase() === currentUser.username.toLowerCase()
              );
              if (existingParticipant) {
                console.log('Already joined, redirecting...');
                setCurrentParticipant(existingParticipant);
                saveCurrentUser(event.id, existingParticipant.id);
                
                // Check if user is admin
                if (existingParticipant.id === event.adminId) {
                  navigate(`/admin/${event.id}`, { replace: true });
                } else {
                  navigate(`/event/${event.id}`, { replace: true });
                }
                return;
              }
            }
          }
        } else {
          setError('Event not found. Please check the link and try again.');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        setError('Failed to load event. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, loadEvent, setEventId, currentUser, isAuthenticated, setCurrentParticipant, navigate]);

  // Check if user is already in the event
  const isUserAlreadyIn = eventData && currentUser && isAuthenticated && eventData.participants.some(p => 
    p.name.toLowerCase() === currentUser.username.toLowerCase()
  );
  
  const existingParticipant = isUserAlreadyIn ? eventData?.participants.find(p => 
    p.name.toLowerCase() === currentUser?.username.toLowerCase()
  ) : null;
  
  const isUserAdmin = existingParticipant && existingParticipant.id === eventData?.adminId;

  const handleJoinEvent = async () => {
    if (!currentUser || !isAuthenticated) {
      setError('You must be logged in to join an event');
      return;
    }
    
    // Check if event is published
    if (eventData && eventData.published === false) {
      setError('This event is not published yet. Please contact the event organizer.');
      return;
    }

    if (!eventData) {
      setError('Event not loaded. Please try again.');
      return;
    }
    
    // If already in event, navigate to it
    if (isUserAlreadyIn && existingParticipant) {
      if (isUserAdmin) {
        navigate(`/admin/${eventData.id}`);
      } else {
        navigate(`/event/${eventData.id}`);
      }
      return;
    }

    setJoining(true);
    setError('');

    try {
      // Create participant with user's username
      const participantId = `participant-${Date.now()}`;
      const participant = createParticipant(
        participantId,
        currentUser.username,
        eventData.id
      );

      // CRITICAL FIX: Reload event to get latest participants (prevent race condition)
      const latestEvent = await loadEventFromFirestore(eventData.id);
      if (!latestEvent) {
        throw new Error('Failed to reload event');
      }

      // Check if already joined (someone may have just joined)
      const alreadyJoined = latestEvent.participants.some(
        p => p.name.toLowerCase() === currentUser.username.toLowerCase()
      );
      
      if (alreadyJoined) {
        console.log('User already joined during reload check');
        const existingP = latestEvent.participants.find(
          p => p.name.toLowerCase() === currentUser.username.toLowerCase()
        );
        if (existingP) {
          saveCurrentUser(latestEvent.id, existingP.id);
          setCurrentParticipant(existingP);
          setEventId(latestEvent.id);
          navigate(`/event/${latestEvent.id}`);
        }
        return;
      }

      // Add participant to event context
      addParticipant(participant);

      // Update event with new participant using LATEST participants
      const updatedEvent = {
        ...latestEvent,
        participants: [...latestEvent.participants, participant],
      };
      
      // Save event to Firestore
      await saveEvent(updatedEvent);
      
      // Link event to user
      await addEventToUser(currentUser.id, eventData.id);
      
      // Save current user for this event
      saveCurrentUser(eventData.id, participant.id);

      // Set user context
      setCurrentParticipant(participant);
      setEventId(eventData.id);

      // Navigate to dashboard
      navigate(`/event/${eventData.id}`);
    } catch (err) {
      setError('Failed to join event. Please try again.');
      console.error('Error joining event:', err);
      setJoining(false);
    }
  };

  const handleLoginClick = () => {
    // Save event ID to session storage for auto-join after login
    if (eventId) {
      sessionStorage.setItem('pendingEventJoin', eventId);
    }
    navigate('/login');
  };

  const handleSignUpClick = () => {
    // Save event ID to session storage for auto-join after signup
    if (eventId) {
      sessionStorage.setItem('pendingEventJoin', eventId);
    }
    navigate('/signup');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold text-primary-text mb-4">
              Event Not Found
            </h1>
            <div className="text-secondary-text mb-6">
              <p>{error || 'The event you\'re looking for doesn\'t exist.'}</p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const getStatusBadge = () => {
    if (eventData.published === false) {
      return <Badge variant="warning" size="sm">Unpublished (Draft)</Badge>;
    }
    if (eventData.finished) {
      return <Badge variant="default" size="sm">Finished</Badge>;
    }
    if (eventData.status === 'live') {
      return <Badge variant="error" size="sm">Live</Badge>;
    }
    return <Badge variant="info" size="sm">Upcoming</Badge>;
  };

  return (
    <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-4xl font-bold text-primary-text">
              {eventData.name}
            </h1>
            {getStatusBadge()}
          </div>
          <p className="text-secondary-text text-lg">
            Join this fight night betting pool
          </p>
        </div>

        <Card padding="lg">
          {/* Event Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-tertiary-bg rounded-lg">
            <div>
              <div className="text-xs text-secondary-text mb-1">Participants</div>
              <div className="font-semibold text-primary-text">
                {eventData.participants.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-secondary-text mb-1">Fights</div>
              <div className="font-semibold text-primary-text">
                {eventData.fights.length}
              </div>
            </div>
          </div>

          {/* Starting Balance Info */}
          <div className="mb-6 p-4 bg-secondary-bg rounded-lg">
            <p className="text-secondary-text text-sm mb-2">
              Starting Balance: <span className="text-primary-text font-bold">$1,000.00</span>
            </p>
            <p className="text-muted-text text-xs">
              You must bet all $1,000 to compete. Only winnings count toward the leaderboard.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-error/10 border border-error rounded-lg mb-4">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Authenticated: Show Join/Enter Button */}
          {isAuthenticated && currentUser ? (
            <div className="space-y-4">
              {isUserAlreadyIn ? (
                <div className="mb-4">
                  <div className="p-3 bg-success-green/10 border border-success-green rounded-lg mb-4">
                    <p className="text-sm text-success-green text-center">
                      ✓ You're already in this event!
                    </p>
                  </div>
                  <p className="text-secondary-text text-sm text-center">
                    Joined as: <span className="text-primary-text font-semibold">{currentUser.username}</span>
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-secondary-text text-sm text-center">
                    You will join as: <span className="text-primary-text font-semibold">{currentUser.username}</span>
                  </p>
                </div>
              )}
              <Button 
                variant={isUserAlreadyIn ? "success" : "primary"}
                fullWidth 
                onClick={handleJoinEvent}
                disabled={joining || eventData.published === false}
              >
                {eventData.published === false ? 'Event Not Published' : (joining ? 'Joining...' : (isUserAlreadyIn ? 'ENTERED - Go to Event' : 'Join Event'))}
              </Button>
            </div>
          ) : (
            /* Not Authenticated: Show Login/Signup Buttons */
            <div className="space-y-4">
              <p className="text-center text-secondary-text mb-4">
                Login or Sign up to Join
              </p>
              <Button 
                variant="primary" 
                fullWidth 
                onClick={handleLoginClick}
              >
                Login
              </Button>
              <Button 
                variant="secondary" 
                fullWidth 
                onClick={handleSignUpClick}
              >
                Sign Up
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

